import UserOutlined from "@ant-design/icons/UserOutlined";
import {
  Button,
  Form,
  Input,
  Layout,
  message,
  Space,
  Spin,
  Typography,
} from "antd";
import { useForm } from "antd/lib/form/Form";
import { LoadingIndicatorWithoutSpin } from "components/common/loading-indicator/loading-indicator";
import { PasswordStrengthIndicators } from "components/modules/user-management/users/components/my-profile/components/password-strength-indicator";
import { push } from "connected-react-router";
import QueueAnim from "rc-queue-anim";
import { useCallback } from "react";
import { useStore } from "react-redux";
import { addRecentLoginData } from "store/modules/app-state/app-state.actions";
import { setPreviousDomain } from "store/modules/preferences/preferences.actions";
import { useUrlQuery } from "utils/hooks/use-url-query";
import { justWait } from "utils/just-wait";
import { useSDK, useSDKActionWithDeps } from "../../../@sdk/sdk.hooks";
import {
  setAccessToken,
  setCurrentUser,
} from "../../../store/modules/users/users.actions";
import "./accept-invitation.page.style.scss";

const { Footer } = Layout;
const { Title } = Typography;

const AcceptInvitationPage = () => {
  const store = useStore();

  const goToDashboard = useCallback(() => store.dispatch(push("/")), [store]);
  const goToLogin = useCallback(() => store.dispatch(push("/login")), [store]);
  const goToRegister = useCallback(
    () => store.dispatch(push("/register")),
    [store],
  );

  const urlQuery = useUrlQuery();
  const secret = urlQuery.get("invitation")!;
  if (!secret) {
    goToLogin();
  }
  const {
    data: invitationData,
    isLoading,
    error,
  } = useSDK((SDK) => SDK.getInvitationData(secret), []);

  const {
    doAction: onFinish,
    isProcessing,
    response,
    dispatch,
  } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (values) =>
        SDK.createAccountWithInviteCode({
          secret,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
        }).then((res) => {
          const enableSubDomains =
            !window.location.href.includes("localhost") &&
            !window.location.href.includes("vercel.app");

          if (enableSubDomains) {
            window.location.href = `https://${res.organization.domain}.clickconnector.io/login?accessToken=${res.accessToken}`;
            return justWait(4000);
          } else {
            SDK.configure({ token: res.accessToken });
            dispatch(setPreviousDomain(res.organization.domain));
            dispatch(setCurrentUser(res.user));
            dispatch(setAccessToken(res.accessToken));
            dispatch(
              addRecentLoginData({
                // Org Data
                domain: res.organization.domain,
                // organizationLogo: string,
                // User Data
                email: res.user.credentials.email,
                firstName: res.user.data.firstName,
                lastName: res.user.data.lastName,
                avatar: res.user.data.avatar,
                // Access Data
                accessToken: res.accessToken,
              }),
            );
            dispatch(push(`/`));
          }
        }),
      successMessage: "Successfully logged in",
      failureMessage: "Something went wrong",
    }),
    [secret],
  );

  const [form] = useForm();

  return (
    <Layout className="layout h-full w-full flex flex-col justify-center items-center">
      <img src="/logo.png" style={{ width: 240 }} alt="" />

      {isLoading && !error && (
        <Spin
          spinning={!error && isLoading}
          indicator={<LoadingIndicatorWithoutSpin />}
          wrapperClassName=""
        >
          <div
            className="mt-8 bg-white dark:bg-gray-900 p-12 rounded-md mode_transition"
            style={{ minHeight: 500, width: 550 }}
          ></div>
        </Spin>
      )}
      {error && (
        <div
          className="mt-8 bg-white dark:bg-gray-900 p-12 rounded-md mode_transition flex-row flex justify-center items-center"
          style={{ minHeight: 500, width: 550 }}
        >
          <span className="font-bold">Invalid Invitation</span>
        </div>
      )}
      {!isLoading && (
        <div
          className="mt-8 bg-white dark:bg-gray-900 p-12 rounded-md mode_transition"
          style={{ minHeight: 500, width: 550 }}
        >
          <QueueAnim delay={600}>
            <Title level={3} key="title">
              Join {invitationData.organizationName}
            </Title>
            <Typography.Paragraph key="description">
              You have been invited by {invitationData.userName} to join the{" "}
              {invitationData.organizationName} Team
            </Typography.Paragraph>
            <Form
              form={form}
              name="normal_login"
              onFinish={async () => {
                try {
                  await form.validateFields();
                  onFinish({
                    ...form.getFieldsValue(),
                  });
                } catch (e) {
                  message.error("Please check your input");
                }
              }}
              layout="vertical"
              key="form"
              // className="animated fadeInRight"
            >
              <Space>
                {/* First Name */}

                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[
                    {
                      required: true,
                      message: "Please input your first name!",
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="First name"
                    size="large"
                  />
                </Form.Item>

                {/* Last Name */}

                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[
                    { required: true, message: "Please input your last name!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="Last name"
                    size="large"
                  />
                </Form.Item>
              </Space>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password!",
                  },
                ]}
                hasFeedback
              >
                <Input.Password placeholder="Password" size="large" />
              </Form.Item>

              <PasswordStrengthIndicators />

              <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Please confirm your password!",
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "The two passwords that you entered do not match!",
                      );
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm Password" size="large" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                  loading={isProcessing}
                  size="large"
                >
                  Continue
                </Button>
                {/* Or{" "}
              <Button type="link" onClick={() => props.goToLogin()}>
                Login
              </Button> */}
              </Form.Item>
            </Form>
          </QueueAnim>
        </div>
      )}

      <Footer style={{ textAlign: "center" }}>
        ClickConnector ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
};

export default AcceptInvitationPage;
